import { LocalDate, nativeJs } from "@js-joda/core";
import moment from "moment";

import {
    declareAiCapability,
    provideAiCapability,
} from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-capability-registry";

// The passport verifier describing its own filter bar. Its three filters are already named in code -
// the page manager registers `from`, `to` and `status`, and the table has a search box - so this says
// out loud what the page always knew, in the words someone would use to ask for it.
declareAiCapability({
    id: "passportVerifier.filter",
    title: "Filter the passport documents to verify",
    effect: "prefill",
    description:
        "Sets the filters on the Passport Verifier page and re-runs its search: the uploaded-date range, the review status (approved, declined, pending) and a free-text search over document name, caregiver name and document type. Anything left out keeps what the page already has, so this can narrow a search that is already on screen. Only works while that page is open.",
    location: "the filter bar of the Passport Verifier page",
    scope: "page",
    permissionKey: "view_passport_verifier_section",
    params: [
        {
            name: "from",
            type: "date",
            required: false,
            description: "Earliest upload date to include, as yyyy-MM-dd."
        },
        {
            name: "to",
            type: "date",
            required: false,
            description: "Latest upload date to include, as yyyy-MM-dd."
        },
        {
            name: "status",
            type: "enum",
            required: false,
            multiple: true,
            description:
                "Limit to documents in this review state. Several are a filter rather than a conflict: approved and declined together means both.",
            options: [
                { value: "APPROVED", label: "Approved" },
                { value: "DECLINED", label: "Declined" },
                { value: "PENDING", label: "Pending" }
            ]
        },
        {
            name: "search",
            type: "text",
            required: false,
            searchesFreeText: true,
            description:
                "Free text the page searches over document name, caregiver name and document type. Use it for a name or word that is not one of the filters above."
        }
    ],
    examples: [
        "show me pending passport documents",
        "passport documents uploaded last month",
        "declined documents for jane"
    ]
});

//! @ngInject
export function passportVerifierCtrl($scope, $rootScope, itemSearchPageManager, NgTableParams, DatabaseApi, toaster, $filter, $uibModal) {
    $scope.tableParams = null;
    $scope.reviewedAtCounter = 0;

    $scope.docStatuses = [
        { id: 1, value: "APPROVED", label: "Approved" },
        { id: 2, value: "DECLINED", label: "Declined" },
        { id: 3, value: "PENDING", label: "Pending" }
    ];

    $scope.documentTypes = [];

    function initialize() {
        initPageManager();
        $scope.loadItems();
    }

    function getDocStatusesByName(items) {
        const statusIds = items.map(item => item.id);
        const statuses = $scope.docStatuses.filter(item => statusIds.includes(item.id));
        return $filter("pipeItems")(statuses, "value");
    }

    var initPageManager = function () {
        $scope.pageManager = itemSearchPageManager.createSearchPageManager("/passport_documents_verification");
        $scope.pageManager.initFromToDateParams();
        $scope.pageManager.initSearchParamDeprecated("status", [], "status", getDocStatusesByName);
    };

    $scope.globalFilter = { val: "" };
    $scope.applyGlobalSearch = function (term) {
        var filter = { $: term };
        if ($scope.tableParams) {
            angular.extend($scope.tableParams.filter(), filter);
        } else {
            console.log('no table');
        }
    };

    var initTable = function (items) {
        items.forEach(function (item) {
            if (item.expiryDate !== null) {
                var d = LocalDate.parse(item.expiryDate);
                item.expiryDate = new Date(d.year(), d.month().value() - 1, d.dayOfMonth());
            }
        });

        var options = {
            count: 25,
            sorting: { createdAt: "desc" }
        };
        $scope.tableParams = new NgTableParams(options, {
            dataset: items
        });

        if ($scope.globalFilter.val) {
            $scope.applyGlobalSearch($scope.globalFilter.val);
        }
    };

    $scope.onExpiryDatePickerChange = function (item) {
        var body = {
            expiryDate: LocalDate.from(nativeJs(moment(item.expiryDate))).toString()
        }

        DatabaseApi.put('agencies/' + $rootScope.agencyId + '/agency_members/' + $rootScope.agencyMemberId
        + '/caregivers/' + item.caregiver + '/passport_documents/' + item.id, body).then(function (res) {
            toaster.pop('success', "Expiry date updated successfully");
        }, function (err) {
            toaster.pop('error', "Failed to update document expiry date");
        });
    }

    $scope.verifyDocument = function (row, status) {
        var body = {
            status: status
        }

        DatabaseApi.put('agencies/' + $rootScope.agencyId + '/agency_members/' + $rootScope.agencyMemberId
            + '/passport_documents/' + row.id + '/update_status', body).then(function (res) {
                row.status = status;
                toaster.pop('success', "Document status updated successfully");
            }, function (err) {
                toaster.pop('error', "Failed to update document status");
            });
    }

    $scope.viewPassportDocument = function (document) {
        var newScope = $scope.$new();
        newScope.document = document;
        newScope.url = 'agencies/' + $rootScope.agencyId + '/agency_members/' + $rootScope.agencyMemberId
        + '/passport_documents/' + document.id + '/file_url';

        $uibModal.open({
            templateUrl: 'admin/views/image-viewer-modal.html',
            size: 'lg',
            controller: 'imageViewerModalCtrl',
            scope: newScope
        });
    }

    $scope.loadItems = function () {
        $scope.pageManager.executeSearch().then(function (response) {
            initTable(response.data.documents);
        }, function (error) {
            toaster.pop("error", "Failed to fetch passport documents");
        });

        const getDocumentTypesUrl = 'agencies/' + $rootScope.agencyId +
            '/agency_members/' + $rootScope.agencyMemberId +
            '/passport_documents/types';
        DatabaseApi.get(getDocumentTypesUrl).then(res => {
            $scope.documentTypes = res.data.documentTypes;
        });
    };

    $scope.incrementReviewedAt = function() {
        $scope.reviewedAtCounter ++;
    }

    $scope.getDocumentTypeById = (documentTypeId) => {
        return $scope.documentTypes.find((documentType) => documentType.id === documentTypeId);
    }

    $scope.updateDocumentTypeId = (row) => {
        const updateDocTypeIdUrl = 'agencies/' + $rootScope.agencyId +
            '/agency_members/' + $rootScope.agencyMemberId +
            '/passport_documents/' + row.id +
            '/update_doc_type';
        const body = {
            documentTypeId: row.docType.id
        };
        DatabaseApi.put(updateDocTypeIdUrl, body).then((res) => {
            row.docType.name = $scope.getDocumentTypeById(row.docType.id).documentName;
            toaster.pop('success', 'Document type updated successfully');
        }, (err) => {
            toaster.pop('error', 'Oops! Something went wrong', 'Failed updating document type');
        });
    }

    // Only navigation and prefilling: this sets the same search params the pickers and dropdown write
    // to, then calls the page's own loadItems. Approving or declining a document stays with the person.
    const releasePassportVerifierFilter = provideAiCapability("passportVerifier.filter", (args) => {
        const applied = [];
        const asList = (value) => (Array.isArray(value) ? value : [value]);
        const asDate = (text) => {
            const parts = String(text).split("-").map(Number);

            if (parts.length !== 3 || parts.some(isNaN)) {
                throw new Error("Could not read the date " + text + ".");
            }

            return new Date(parts[0], parts[1] - 1, parts[2]);
        };

        if (args.from !== undefined) {
            $scope.pageManager.searchParams.from.value = asDate(args.from);
        }

        if (args.to !== undefined) {
            $scope.pageManager.searchParams.to.value = asDate(args.to);
        }

        if (args.from !== undefined || args.to !== undefined) {
            applied.push([args.from, args.to].filter(Boolean).join(" to "));
        }

        if (args.status !== undefined) {
            const wanted = asList(args.status).map((one) => String(one).toUpperCase());
            const statuses = $scope.docStatuses.filter((status) => wanted.includes(status.value));

            if (statuses.length === 0) {
                throw new Error("A passport document is approved, declined or pending - not " + args.status + ".");
            }

            $scope.pageManager.searchParams.status.value = statuses;
            applied.push(statuses.map((status) => status.label).join(" and "));
        }

        // Set before the reload on purpose: initTable re-applies whatever is in globalFilter, so the
        // term survives the new dataset instead of being cleared by it.
        if (args.search !== undefined) {
            $scope.globalFilter.val = String(args.search);
            applied.push('"' + String(args.search) + '"');
        }

        $scope.loadItems();

        return applied.length === 0
            ? "Re-ran the passport document search"
            : "Showing passport documents: " + applied.join(", ");
    });

    $scope.$on("$destroy", releasePassportVerifierFilter);

    initialize();
};
